import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../utils/cn";

/**
 * Solomon Spinner — custom circular-border rotation pattern, not shadcn's
 * lucide-Loader2Icon variant. Brand calls for a solid ring with one segment
 * highlighted (the spinning arc).
 *
 * Surface variants:
 *   - light  (default) — ring = line-base,           arc = foreground-base
 *   - dark             — ring = line-inverse-muted,  arc = foreground-inverse
 *   - accent           — ring = surface-sunken,      arc = accent
 */
const spinnerVariants = cva("inline-block rounded-full animate-spin", {
    variants: {
        size: {
            sm: "size-4 border-2",
            md: "size-5 border-2",
            lg: "size-6 border-[3px]",
            xl: "size-10 border-4"
        },
        surface: {
            light: "border-line-base border-t-foreground-base",
            dark: "border-line-inverse-muted border-t-foreground-inverse",
            accent: "border-surface-sunken border-t-accent"
        }
    },
    defaultVariants: {
        size: "md",
        surface: "light"
    }
});

function Spinner({
    className,
    size,
    surface,
    ...props
}: React.HTMLAttributes<HTMLSpanElement> &
    VariantProps<typeof spinnerVariants>): React.ReactElement {
    return (
        <span
            data-slot="spinner"
            data-surface={surface ?? "light"}
            role="status"
            aria-label="Loading"
            className={cn(spinnerVariants({ size, surface }), className)}
            {...props}
        />
    );
}

export { Spinner, spinnerVariants };
